// Iron Beetle Armoury - Form Layer Web Component
// Mirrors inputs from a target form into its own rendered UI and keeps values in sync

(function () {
  class IBFormLayer extends HTMLElement {
    static get observedAttributes() {
      return ["target"];
    }

    constructor() {
      super();

      setTimeout(() => {
        this.attachShadow({ mode: "open" });
        this._mirrorToOriginal = new Map();
        this._originalToMirror = new Map();
        this._targetRoot = null; // container (host) that holds the form (possibly in shadow)
        this._searchRoot = null; // where we query inside (shadowRoot or element)
        this._targetForm = null;
        this._rowTemplate = document.createElement("template");
        this._rowTemplate.innerHTML = `
				<div class="ibfl-row">
					<label class="ibfl-label"></label>
					<input class="ibfl-input" />
				</div>
			`;
        this._renderBase();
      }, 5000);
    }

    connectedCallback() {
      setTimeout(() => {
        this._initConnect();
      }, 5000);
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "target" && oldVal !== newVal) {
        this._connect();
      }
    }

    disconnectedCallback() {
      this._teardownListeners();
      this._stopWaitForForm();
    }

    _renderBase() {
      const style = document.createElement("style");
      style.textContent = `
				:host { display: block; }
				.ibfl { display: grid; gap: 12px; }
				.ibfl-row { display: grid; gap: 6px; }
				.ibfl-label { font-size: 0.9rem; color: #374151; }
				.ibfl-input {
					padding: 10px 12px;
					border: 1px solid #d1d5db;
					border-radius: 6px;
					font-size: 1rem;
				}
				.ibfl-actions { display: flex; justify-content: flex-end; margin-top: 8px; }
				.ibfl-submit {
					appearance: none;
					padding: 10px 14px;
					border: 1px solid #1f2937;
					background: #111827;
					color: #fff;
					border-radius: 6px;
					font-size: 0.95rem;
					cursor: pointer;
				}
				.ibfl-submit:disabled { opacity: 0.6; cursor: not-allowed; }
			`;
      const container = document.createElement("div");
      container.className = "ibfl";
      container.innerHTML = `
				<!-- Fields will be rendered here -->
				<div class="ibfl-fields"></div>
				<div class="ibfl-actions">
					<button type="button" class="ibfl-submit">Submit</button>
				</div>
			`;
      this.shadowRoot.append(style, container);
    }

    _connect() {
      this._teardownListeners();
      this._targetRoot = this._resolveTargetRoot();
      this._searchRoot = this._resolveSearchRoot(this._targetRoot);
      this._targetForm = this._searchRoot
        ? this._searchRoot.querySelector("form")
        : null;
      this._renderFields();
      this._attachListeners();
    }

    _initConnect() {
      // Try immediately
      this._connect();
      if (this._targetForm) return;
      // If not ready, start observers/polling similar to measurement-form-fill
      this._startWaitForForm();
    }

    _resolveTargetRoot() {
      const attr = this.getAttribute("target");
      if (!attr) return null;
      // Allow both CSS selector and bare id
      let el = null;
      if (attr.startsWith("#") && attr.length > 1) {
        // Prefer getElementById to avoid CSS selector edge-cases with special chars in IDs
        el =
          document.getElementById(attr.slice(1)) ||
          document.querySelector(attr);
      } else if (/^[A-Za-z][-\w:.]*$/.test(attr)) {
        // Looks like a bare id
        el =
          document.getElementById(attr) ||
          document.querySelector(`#${CSS.escape(attr)}`);
      } else {
        // Fallback to querySelector for arbitrary selectors
        try {
          el = document.querySelector(attr);
        } catch (_) {
          el = null;
        }
      }
      return el || null;
    }

    _resolveSearchRoot(el) {
      if (!el) return null;
      // Prefer querying inside the shadow root if present
      return el.shadowRoot || el;
    }

    _startWaitForForm() {
      this._stopWaitForForm();
      const tryConnect = () => {
        this._connect();
        if (this._targetForm) {
          this._stopWaitForForm();
        }
      };
      // Document-level observer to catch host additions quickly
      this._docObserver = new MutationObserver(() => tryConnect());
      try {
        this._docObserver.observe(document.documentElement, { childList: true, subtree: true });
      } catch (_) {}
      // Lightweight polling to catch shadowRoot/content changes (not observable from light DOM)
      let elapsed = 0;
      const intervalMs = 250;
      const maxMs = 15000;
      this._pollId = setInterval(() => {
        elapsed += intervalMs;
        tryConnect();
        if (this._targetForm || elapsed >= maxMs) this._stopWaitForForm();
      }, intervalMs);
    }

    _stopWaitForForm() {
      if (this._docObserver) {
        try { this._docObserver.disconnect(); } catch (_) {}
        this._docObserver = null;
      }
      if (this._pollId) {
        clearInterval(this._pollId);
        this._pollId = null;
      }
    }

    _renderFields() {
      const fieldsHost = this.shadowRoot.querySelector(".ibfl-fields");
      if (!fieldsHost) return;
      fieldsHost.textContent = "";
      this._mirrorToOriginal.clear();
      this._originalToMirror.clear();

      if (!this._targetForm) return;

      const inputs = Array.from(
        this._targetForm.querySelectorAll("input, select, textarea")
      ).filter((el) => {
        if (!(el instanceof HTMLElement)) return false;
        const type = (el.getAttribute("type") || "").toLowerCase();
        // Exclude non-data controls
        return ![
          "hidden",
          "submit",
          "button",
          "reset",
          "image",
          "file",
        ].includes(type);
      });

      inputs.forEach((originalEl, index) => {
        const row = this._rowTemplate.content.cloneNode(true);
        const labelEl = row.querySelector(".ibfl-label");
        const inputEl = row.querySelector(".ibfl-input");

        // Derive label text
        labelEl.textContent =
          this._getLabelTextFor(originalEl) ||
          this._fallbackLabelFor(originalEl, index);

        // Configure mirror input to resemble original
        this._copyInputPresentation(originalEl, inputEl);

        // Map and initial sync
        this._mirrorToOriginal.set(inputEl, originalEl);
        this._originalToMirror.set(originalEl, inputEl);
        this._syncMirrorFromOriginal(originalEl, inputEl);

        fieldsHost.appendChild(row);
      });
    }

    _getLabelTextFor(input) {
      // 1) aria-labelledby → id → text
      const aria = input.getAttribute("aria-labelledby");
      if (aria) {
        const ids = String(aria).trim().split(/\s+/);
        const root =
          (input.getRootNode && input.getRootNode()) ||
          this._searchRoot ||
          document;
        let text = "";
        ids.forEach((id) => {
          try {
            const el =
              root && typeof root.querySelector === "function"
                ? root.querySelector(`#${CSS.escape(id)}`)
                : document.getElementById(id);
            if (el && el.textContent)
              text += (text ? " " : "") + el.textContent.trim();
          } catch (_) {}
        });
        if (text) return text;
      }
      // 2) <label for="...">
      const id = input.getAttribute("id");
      if (id) {
        const lbl = this._targetForm
          ? this._targetForm.querySelector(`label[for="${CSS.escape(id)}"]`)
          : null;
        if (lbl && lbl.textContent) return lbl.textContent.trim();
      }
      // 3) nearest label in same container
      const container = input.closest("div, fieldset, label");
      if (container) {
        const lbl = container.querySelector("label");
        if (lbl && lbl.textContent) return lbl.textContent.trim();
      }
      return "";
    }

    _fallbackLabelFor(input, idx) {
      const name =
        input.getAttribute("name") ||
        input.getAttribute("id") ||
        `field-${idx + 1}`;
      return name
        .replace(/[\-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    _copyInputPresentation(from, to) {
      const tag = from.tagName.toLowerCase();
      if (tag === "textarea") {
        to.replaceWith((to = document.createElement("textarea")));
        to.className = "ibfl-input";
      }
      if (tag === "select") {
        const select = document.createElement("select");
        select.className = "ibfl-input";
        // copy options
        Array.from(from.options).forEach((opt) => {
          const o = document.createElement("option");
          o.value = opt.value;
          o.textContent = opt.textContent;
          select.appendChild(o);
        });
        to.replaceWith((to = select));
      }

      // Common attributes
      [
        "type",
        "name",
        "value",
        "placeholder",
        "min",
        "max",
        "step",
        "inputmode",
      ].forEach((attr) => {
        const v = from.getAttribute(attr);
        if (v != null) to.setAttribute(attr, v);
      });
      if (from.hasAttribute("required")) to.setAttribute("required", "");
      if (from.hasAttribute("pattern"))
        to.setAttribute("pattern", from.getAttribute("pattern"));
      // Provide a stable id for the mirror input for accessibility
      const safeId = (from.id ? String(from.id) : `field`) + `-mirror`;
      try {
        to.id = safeId;
      } catch (_) {
        /* ignore */
      }
    }

    _attachListeners() {
      if (!this._targetForm) return;
      // Listen to mirror inputs → write to original
      this.shadowRoot.addEventListener("input", this._onMirrorInput);
      this.shadowRoot.addEventListener("change", this._onMirrorInput);
      // Listen for submit button click inside shadow
      this._onShadowClick = (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        if (target.classList.contains('ibfl-submit')) {
          this._handleSubmitClick();
        }
      };
      this.shadowRoot.addEventListener('click', this._onShadowClick);
      // Listen to original inputs → update mirror (in case original changes externally)
      this._origHandler = (e) => {
        const el = e.target;
        if (!this._originalToMirror.has(el)) return;
        const mirror = this._originalToMirror.get(el);
        this._syncMirrorFromOriginal(el, mirror);
      };
      this._targetForm.addEventListener("input", this._origHandler, true);
      this._targetForm.addEventListener("change", this._origHandler, true);
    }

    _teardownListeners() {
      this.shadowRoot.removeEventListener("input", this._onMirrorInput);
      this.shadowRoot.removeEventListener("change", this._onMirrorInput);
      if (this._onShadowClick) {
        this.shadowRoot.removeEventListener('click', this._onShadowClick);
        this._onShadowClick = null;
      }
      if (this._targetForm && this._origHandler) {
        this._targetForm.removeEventListener("input", this._origHandler, true);
        this._targetForm.removeEventListener("change", this._origHandler, true);
      }
    }

    _handleSubmitClick() {
      if (!this._targetForm) return;
      // Ensure mirrors are flushed → originals
      this._mirrorToOriginal.forEach((original, mirror) => {
        this._syncOriginalFromMirror(mirror, original);
      });
      // Prefer clicking the native submit control if present
      let submitEl = null;
      try {
        submitEl = this._targetForm.querySelector('button[type="submit"]:not([disabled])')
          || this._targetForm.querySelector('input[type="submit"]:not([disabled])');
      } catch (_) {}
      if (submitEl && typeof submitEl.click === 'function') {
        submitEl.click();
        return;
      }
      // Fallbacks
      if (typeof this._targetForm.requestSubmit === 'function') {
        this._targetForm.requestSubmit();
      } else {
        this._targetForm.submit();
      }
    }

    _onMirrorInput = (e) => {
      const mirror = e.target;
      if (!(mirror instanceof HTMLElement)) return;
      if (!this._mirrorToOriginal.has(mirror)) return;
      const original = this._mirrorToOriginal.get(mirror);
      this._syncOriginalFromMirror(mirror, original);
    };

    _syncMirrorFromOriginal(original, mirror) {
      const tag = original.tagName.toLowerCase();
      if (tag === "select") {
        mirror.value = original.value;
        return;
      }
      const type = (original.getAttribute("type") || "").toLowerCase();
      if (type === "checkbox" || type === "radio") {
        mirror.checked = original.checked;
      } else {
        mirror.value = original.value;
      }
    }

    _syncOriginalFromMirror(mirror, original) {
      const tag = original.tagName.toLowerCase();
      if (tag === "select") {
        original.value = mirror.value;
        original.dispatchEvent(new Event("input", { bubbles: true }));
        original.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }
      const type = (original.getAttribute("type") || "").toLowerCase();
      if (type === "checkbox" || type === "radio") {
        // Handle radio groups: mirror.name should match original.name for value parity, but we only sync the checked state for the paired element
        original.checked = mirror.checked;
        // For radios, ensure group state reflects properly by syncing siblings when checked
        if (type === "radio" && mirror.checked && original.name) {
          const root = this._targetForm || original.getRootNode();
          try {
            const group =
              root && typeof root.querySelectorAll === "function"
                ? root.querySelectorAll(
                    `input[type="radio"][name="${CSS.escape(original.name)}"]`
                  )
                : [];
            group.forEach((r) => {
              if (r !== original && r.checked) {
                r.checked = false;
                r.dispatchEvent(new Event("change", { bubbles: true }));
              }
            });
          } catch (_) {}
        }
      } else {
        original.value = mirror.value;
      }
      original.dispatchEvent(new Event("input", { bubbles: true }));
      original.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  customElements.define("ib-form-layer", IBFormLayer);
})();
